import type OlMap from "ol/Map";
import { always } from "ol/events/condition";
import DragZoom from "ol/interaction/DragZoom";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { ToggleControl, useNgwMapContext } from "@nextgis/react-ngw-map";
import type { ControlOptions, ToggleControlOptions } from "@nextgis/webmap";

import { useTranslation } from "../../i18n";

import { ZoomInIcon, ZoomOutIcon } from "./icons";

type ToolZoomProps = Omit<ToggleControlOptions, "html" | "onClick" | "title"> &
  ControlOptions & {
    out?: boolean;
  };

export function ToolZoom({ out = false, ...controlProps }: ToolZoomProps) {
  const { t } = useTranslation();
  const { ngwMap } = useNgwMapContext();
  const interactionRef = useRef<DragZoom | null>(null);
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;

  const title = useMemo(() => (out ? t("zoom.out") : t("zoom.in")), [out, t]);

  useEffect(() => {
    if (!olMap) return;

    const interaction = new DragZoom({ condition: always, out });
    interaction.setActive(false);
    olMap.addInteraction(interaction);
    interactionRef.current = interaction;

    return () => {
      interaction.setActive(false);
      olMap.removeInteraction(interaction);
      interactionRef.current = null;
      olMap.getTargetElement().style.cursor = "auto";
    };
  }, [olMap, out]);

  const setActive = useCallback(
    (active: boolean) => {
      const interaction = interactionRef.current;
      if (!interaction || !olMap) return;

      interaction.setActive(active);
      olMap.getTargetElement().style.cursor = active
        ? out
          ? "zoom-out"
          : "zoom-in"
        : "auto";
    },
    [olMap, out],
  );

  return (
    <ToggleControl {...controlProps} title={title} onClick={setActive}>
      {out ? (
        <ZoomOutIcon className="tool-svg" />
      ) : (
        <ZoomInIcon className="tool-svg" />
      )}
    </ToggleControl>
  );
}
